import clsx from 'clsx'
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, CircleCheckIcon, HelpCircleIcon, XCircleIcon } from 'lucide-react'
import type React from 'react'
import { useMemo, useState } from 'react'

import { Badge, Popover } from 'slab'
import type { MemberOnboardingMedicalPlanDisplayItemInNetworkVerificationProgressStatus, MemberOnboardingProviderInNetworkMap, MemberOnboardingProviderInNetworkMapInNetwork } from 'utils/web_api.schemas'

const InNetworkStatusIcon: React.FC<{
  status: MemberOnboardingProviderInNetworkMapInNetwork
  size?: 'small'
}> = ({ status, size }) => {
  const iconSize = size === 'small' ? 12 : 14
  switch (status) {
    case 'IN_NETWORK':
      return (
        <CircleCheckIcon className='text-green-400' size={iconSize}>
          <title>In network</title>
        </CircleCheckIcon>
      )
    case 'OUT_OF_NETWORK':
      return (
        <XCircleIcon className='text-red-400' size={iconSize}>
          <title>Out of network</title>
        </XCircleIcon>
      )
    default:
      return (
        <HelpCircleIcon className='text-yellow-400' size={iconSize}>
          <title>Unknown</title>
        </HelpCircleIcon>
      )
  }
}

const ProviderStatusLabels: React.FC<{
  status: MemberOnboardingProviderInNetworkMapInNetwork
  names: string[]
}> = ({ status, names }) => {
  return (
    <ul>
      {
        names.map(name => (
          <li className='flex flex-row gap-1 items-center' key={name}>
            <InNetworkStatusIcon status={status} size='small' />
            {name}
          </li>
        ))
      }
    </ul>
  )
}

const InNetworkVerificationProgressBadge: React.FC<{
  inNetworkVerificationProgressStatus: MemberOnboardingMedicalPlanDisplayItemInNetworkVerificationProgressStatus;
}> = ({ inNetworkVerificationProgressStatus }) => {
  if (inNetworkVerificationProgressStatus === 'not_started') return null
  if (inNetworkVerificationProgressStatus === 'in_progress') {
    return <Badge bordered type='thatch'>Verification in progress</Badge>
  }
  return <Badge bordered type='thatch'>Verification completed</Badge>
}

const Divider: React.FC = () => {
  return <div className='w-full h-1px bg-color-stroke-light last:hidden' />
}

export const linkClasses = clsx(
  'body-sm',
  'font-semibold',
  'text-nowrap',
  'text-gray-800',
  'hover:text-gray-800',
  'border-b-2',
  'border-dotted',
  'border-gray-400',
  'hover:border-gray-800',
  'transition',
  'outline',
  'outline-2',
  'outline-offset-2',
  'outline-transparent',
  'focus-visible:outline-thatch-500'
)

const LaunchFerryVerificationTaskTakeover: React.FC<{
  onPlanVerification: () => void;
}> = ({ onPlanVerification }) => (
  <div>
    <span className='body-sm'>You can try calling your providers, or we can help. </span>
    <span className='cursor-pointer'>
      <button onClick={onPlanVerification}>
        <span className={linkClasses}>Get started</span>
        <ArrowRightIcon size={14} className='ml-1 inline text-gray-400' />
      </button>
    </span>
  </div>
)

const SelfServeInNetworkVerificationLink: React.FC<{
  verifyingInNetworkStatusPlanIds: string[];
  providerSupportLink?: string;
}> = ({ verifyingInNetworkStatusPlanIds, providerSupportLink }) => {
  const label = `We're verifying your providers on ${verifyingInNetworkStatusPlanIds.length} other plans. You can try verifying this one on your one.`

  return (
    <div>
      <span className='body-sm'>{label}</span>
      <a
        href={providerSupportLink}
        target='_blank'
        rel='noopener noreferrer'
        data-testid='support-link'
      >
        <span
          className={linkClasses}
        >
          Learn how
        </span>
        <span>
          <ArrowRightIcon size={14} className='ml-1 inline text-gray-400' />
        </span>
      </a>
    </div>
  )
}

export const ProviderInNetworkVerification: React.FC<{
  providerInNetworkMap: MemberOnboardingProviderInNetworkMap[];
  inNetworkVerificationProgressStatus: MemberOnboardingMedicalPlanDisplayItemInNetworkVerificationProgressStatus;
  inNetworkProvidersCount: number;
  verifyingInNetworkStatusPlanIds?: string[];
  providerSupportLink?: string;
  onPlanVerification?: () => void;
}> = ({ providerInNetworkMap, inNetworkProvidersCount, inNetworkVerificationProgressStatus, verifyingInNetworkStatusPlanIds, providerSupportLink, onPlanVerification }) => {
  const [open, setOpen] = useState(false)
  const totalProvidersCount = providerInNetworkMap.length
  const label = `${inNetworkProvidersCount}/${totalProvidersCount} ${totalProvidersCount === 1 ? 'provider' : 'providers'} in-network`
  const canKickOffInNetworkVerificationTask = verifyingInNetworkStatusPlanIds && verifyingInNetworkStatusPlanIds.length === 0

  const { inNetworkProviders, outOfNetworkProviders, unknownProviders, status } = useMemo(() => {
    const inNetworkProviders: string[] = []
    const outOfNetworkProviders: string[] = []
    const unknownProviders: string[] = []
    providerInNetworkMap.map(({ name, in_network: inNetwork }) => {
      switch (inNetwork) {
        case 'IN_NETWORK':
          return inNetworkProviders.push(name)
        case 'OUT_OF_NETWORK':
          return outOfNetworkProviders.push(name)
        default:
          return unknownProviders.push(name)
      }
    })
    let status: MemberOnboardingProviderInNetworkMapInNetwork = 'UNKNOWN'
    if (inNetworkProviders.length > 0) {
      status = 'IN_NETWORK'
    }
    if (outOfNetworkProviders.length > inNetworkProviders.length) {
      status = 'OUT_OF_NETWORK'
    }
    return {
      inNetworkProviders,
      outOfNetworkProviders,
      unknownProviders,
      status
    }
  }, [providerInNetworkMap])

  return (
    <>
      <Popover
        onOpenChange={setOpen}
        trigger={
          <button
            className={
              clsx(
                'flex gap-1 items-center body-bold text-gray-500 hover:text-gray-800 focus-visible:text-gray-800 rounded-md',
                'outline outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500',
                'transition-all'
              )
            }
          >
            <InNetworkStatusIcon status={status} />
            <span>{label}</span>
            <span className='text-gray-400 group-hover/provider-trigger:text-gray-500 group-focus-visible/provider-trigger:text-gray-500 transition'>
              {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
            </span>
          </button>
        }
      >
        <div className='p-4 flex flex-col gap-2 w-[250px]'>
          <div className='text-main body-bold'>
            {label}
          </div>
          <div>
            <InNetworkVerificationProgressBadge inNetworkVerificationProgressStatus={inNetworkVerificationProgressStatus} />
          </div>
          {
            inNetworkProviders.length > 0 && (
              <>
                <ProviderStatusLabels status='IN_NETWORK' names={inNetworkProviders} />
                <Divider />
              </>
            )
          }
          {
            outOfNetworkProviders.length > 0 && (
              <>
                <ProviderStatusLabels status='OUT_OF_NETWORK' names={outOfNetworkProviders} />
                <Divider />
              </>
            )
          }
          {
            unknownProviders.length > 0 && (
              <>
                <ProviderStatusLabels status='UNKNOWN' names={unknownProviders} />
              </>
            )
          }
          {
            canKickOffInNetworkVerificationTask && inNetworkVerificationProgressStatus === 'not_started' && onPlanVerification && (
              <LaunchFerryVerificationTaskTakeover onPlanVerification={onPlanVerification} />
            )
          }
          {
            !canKickOffInNetworkVerificationTask && inNetworkVerificationProgressStatus === 'not_started' && onPlanVerification && verifyingInNetworkStatusPlanIds && (
              <SelfServeInNetworkVerificationLink verifyingInNetworkStatusPlanIds={verifyingInNetworkStatusPlanIds} providerSupportLink={providerSupportLink} />
            )
          }
        </div>
      </Popover>
      <div>
        <InNetworkVerificationProgressBadge inNetworkVerificationProgressStatus={inNetworkVerificationProgressStatus} />
      </div>
    </>
  )
}
